/**
 * User rating utilities.
 *
 * Composite rating combines three signals into a single 0-100 score:
 *  - Successful deals (transactions completed): 40% weight
 *  - Listings posted (activity on platform):     20% weight
 *  - Average review score (1-5 from other users): 40% weight
 *
 * The average review is available only after at least one completed deal
 * with the reviewer.
 */

/**
 * Compute a composite trust/rating score from user data.
 * @param {Object} user - user document fields
 * @returns {number} 0-100
 */
export function computeUserRating(user) {
  if (!user) return 0;
  const deals = Math.max(0, Number(user.totalAdsSold) || 0);
  const listings = Math.max(0, Number(user.totalAdsPosted) || 0);
  const ratingSum = Math.max(0, Number(user.ratingSum) || 0);
  const ratingCount = Math.max(0, Number(user.ratingCount) || 0);
  const avgRating = ratingCount > 0 ? ratingSum / ratingCount : 0; // 0-5

  // Normalize each signal to 0-1
  const dealsScore = Math.min(1, deals / 20);         // 20+ deals = full score
  const listingsScore = Math.min(1, listings / 30);   // 30+ listings = full score
  const reviewScore = avgRating / 5;                    // 5 = full score

  // Weighted composite
  const composite = dealsScore * 0.4 + listingsScore * 0.2 + reviewScore * 0.4;
  return Math.round(composite * 100);
}

/**
 * Get a human-readable rating label for a score.
 */
export function getRatingLabel(score, t) {
  if (score >= 80) return t ? t('ratings.excellent') : 'Excellent';
  if (score >= 60) return t ? t('ratings.good') : 'Good';
  if (score >= 40) return t ? t('ratings.fair') : 'Fair';
  if (score >= 20) return t ? t('ratings.newcomer') : 'Newcomer';
  return t ? t('ratings.unrated') : 'Unrated';
}

/**
 * Get average star rating from user data.
 */
export function getAverageRating(user) {
  if (!user) return 0;
  const sum = Number(user.ratingSum) || 0;
  const count = Number(user.ratingCount) || 0;
  return count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
}
